import maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import { BASIS, mitBasis } from '../data/basis.js';

/**
 * Woher die Vektorkacheln kommen.
 *
 * Ohne Konfiguration: OpenFreeMap (öffentlich, ohne Schlüssel).
 * Mit `VITE_TILES_URL`: eigene Ausspielung. Endet die URL auf `.pmtiles`,
 * wird das Archiv direkt per HTTP-Range-Request gelesen – dafür genügt ein
 * Static-Host, es braucht keinen Kachelserver.
 */
const env = import.meta.env || {};

const DEFAULTS = {
  tiles: 'https://tiles.openfreemap.org/planet',
  glyphs: '/fonts/{fontstack}/{range}.pbf',
  sprite: 'https://tiles.openfreemap.org/sprites/ofm_f384/ofm'
};

/**
 * MapLibre nimmt bei Sprite und Glyphen nur absolute URLs. Bewusst per
 * Zeichenkette zusammengesetzt: `new URL()` wuerde die Platzhalter
 * {fontstack} und {range} prozent-kodieren, und dann findet MapLibre sie nicht.
 */
const absolute = (url) => {
  if (/^https?:\/\//.test(url)) return url;
  const pfad = url.startsWith('/') ? url : `/${url}`;
  // In der Android-App steht die Datenbasis fest; im Browser ist es der
  // eigene Host.
  if (BASIS) return BASIS + pfad;
  if (typeof location === 'undefined') return url;
  return location.origin + pfad;
};

const tilesUrl = mitBasis(env.VITE_TILES_URL || DEFAULTS.tiles);
const treesUrl = mitBasis(env.VITE_TREES_URL ?? '/tiles/trees.pmtiles');
const hoehenUrl = mitBasis(env.VITE_HOEHEN_URL ?? '/tiles/hoehenlinien.pmtiles');
export const isPmtiles = tilesUrl.endsWith('.pmtiles');

// Unabhaengig davon registrieren: auch wenn die Hauptkacheln von aussen
// kommen, liegen die Baeume als eigenes Archiv bei uns.
maplibregl.addProtocol('pmtiles', new Protocol().tile);

export const GLYPHS = absolute(env.VITE_GLYPHS_URL || DEFAULTS.glyphs);
export const SPRITE = absolute(env.VITE_SPRITE_URL || DEFAULTS.sprite);

export const OSM_ATTRIBUTION =
  '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">&copy; OpenStreetMap</a>' +
  (isPmtiles || env.VITE_TILES_URL
    ? ''
    : ' · <a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a>');

/**
 * Höhenlinien fürs Ausland. Schweiz und Liechtenstein fehlen darin bewusst -
 * dort zeichnet swisstopo. Erzeugt von deploy/08-build-hoehenlinien.sh.
 */
export function hoehenSource() {
  if (!hoehenUrl) return null;
  return {
    type: 'vector',
    url: `pmtiles://${hoehenUrl}`,
    attribution:
      '<a href="https://doi.org/10.5270/ESA-c5d3d65" target="_blank" rel="noopener">Copernicus DEM</a>'
  };
}

/** Eigenes kleines Archiv mit Einzelbäumen; leerer Wert schaltet es ab. */
export function treeSource() {
  if (!treesUrl) return null;
  return {
    type: 'vector',
    url: `pmtiles://${treesUrl}`,
    attribution: OSM_ATTRIBUTION
  };
}

/** Quellen-Definition für den Style */
export function vectorSource() {
  return isPmtiles
    ? { type: 'vector', url: `pmtiles://${tilesUrl}`, attribution: OSM_ATTRIBUTION }
    : { type: 'vector', url: tilesUrl, attribution: OSM_ATTRIBUTION };
}
